import { createContext } from 'react'

import type {
  MedicationFile,
  MedicationFileAttachment,
  MedicationSchemeStatus,
  MedicationStatement,
} from '@/modules/app/types/HealthParameter.types'

/**
 * A {@link MedicationStatement} extended with a client-only identifier for
 * stable identity in the UI (React keys, edit/delete targeting).
 *
 * The `_clientId` is never written to Formik so it cannot reach the API.
 */
export type IdentifiedMedicationStatement = MedicationStatement & {
  _clientId: string
}

/**
 * Minimal representation of a medication selected via search or free-text
 * entry. Used purely for UI state within the add/edit form — not persisted.
 */
export type SelectedMedication = {
  name: string
  atcCode?: string | null
  atcName?: string | null
}

/**
 * Shared state for the {@link MedicationQuestion} compound component.
 *
 * Provided by `MedicationQuestion` and consumed by its sub-components
 * (`MedicationDialogContent`) so they can read and mutate the medication
 * list without prop drilling.
 */
export type MedicationContextValue = {
  /** The Formik field name / questionnaire linkId for this question. */
  questionId: string

  /** Current medication list with stable client-side IDs. */
  medications: IdentifiedMedicationStatement[]

  /** Current scheme status (unknown, confirmed-empty, recorded). */
  schemeStatus: MedicationSchemeStatus

  /**
   * Whether this question offers photo capture at all, decided by the mime
   * types configured on it. Independent of the parsing feature flag: the flag
   * only decides whether a captured photo is read by the parser.
   */
  isPhotoCaptureEnabled: boolean

  /** Photos that produced no medication, handed to the care team as-is. */
  schemeFiles: MedicationFileAttachment[]

  /** Keep a photo the parser could not read a single medication from. */
  addSchemeFile: (photo: MedicationFileAttachment) => void

  /** Drop a kept photo from the scheme. */
  removeSchemeFile: (fileId: string) => void

  /** The scheme's stored photos, keyed by fileId: their display url, or when they were processed; a photo whose url is not in yet is absent. */
  storedFiles: ReadonlyMap<string, MedicationFile>

  /** Add a new medication to the list. */
  addMedication: (medication: MedicationStatement) => void

  /** Replace an existing medication identified by its `_clientId`. */
  editMedication: (id: string, medication: MedicationStatement) => void

  /** Mark a medication as deleted (or fully remove if newly inserted). */
  removeMedication: (id: string) => void

  /** Set scheme to `confirmed-empty` and advance the questionnaire. */
  confirmNoMedication: () => void

  /** Switch between the choice and form views. */
  setView: (view: 'choice' | 'form') => void

  /**
   * Whether the dialog shows the photo info panel instead of the form. Kept
   * here because the dialog renders the title, and the panel carries its own.
   */
  isPhotoInfoOpen: boolean

  /** Open or close the photo info panel. */
  setPhotoInfoOpen: (isOpen: boolean) => void

  /** Persist the current medication list and advance to the next question. */
  onSubmitAndAdvance: () => void
}

export const MedicationContext = createContext<MedicationContextValue | null>(null)
